import { useI18n } from 'solid-compose';
import { batch, createEffect, createSignal, Index, mergeProps, onCleanup, onMount, Show } from 'solid-js';
import { clickOutside, pressEscape } from '../../helpers/directives';
import FieldError from '../form/FieldError';
import { useForm } from '../form/Form';
import { useFormResetter } from '../form/FormResetter';
import getPositionMenuFun from './getPositionMenuFun';
import styles from './Select.module.css';
// TS workaround for Solid custom directives
false && clickOutside;
false && pressEscape;
function MultiSelect(propsWithoutDefaults) {
    let rootRef;
    const translate = useI18n();
    const formContextValue = useForm();
    const formResetterContextValue = useFormResetter();
    const [validatedOnce, setValidatedOnce] = createSignal(false);
    const props = mergeProps({
        maxChoices: propsWithoutDefaults.options.length,
        optionId: (option) => option,
        optionText: (option) => option,
        renderSelectedOptions: (options) => options.map((option) => props.optionText(option)).join(', '),
        selectedOptions: initialOptions(propsWithoutDefaults)
    }, propsWithoutDefaults);
    let inputElement;
    const [shouldShow, setShouldShow] = createSignal(false);
    const [hoveredOption, setHoveredOption] = createSignal();
    const [error, setError] = createSignal('');
    const [isInputMouseDown, setInputMouseDown] = createSignal(false);
    const [isOptionMouseDown, setOptionMouseDown] = createSignal(false);
    let positionMenu;
    onMount(() => {
        positionMenu = getPositionMenuFun({ inputElement, styles });
    });
    if (formContextValue) {
        onMount(() => {
            const removeElement = formContextValue.addElement({
                ref: rootRef,
                validate: () => {
                    validate();
                    return error() === '';
                }
            });
            onCleanup(removeElement);
        });
    }
    if (formResetterContextValue) {
        onMount(() => {
            const removeElement = formResetterContextValue.addElement({
                reset: () => {
                    batch(() => {
                        setValidatedOnce(false);
                        setShouldShow(false);
                        setHoveredOption(undefined);
                        setError('');
                    });
                }
            });
            onCleanup(removeElement);
        });
    }
    function validate() {
        setValidatedOnce(true);
        if (props.required && props.selectedOptions.length === 0) {
            setError(translate('select_option'));
            return;
        }
        setError('');
    }
    createEffect(() => {
        shouldShow();
        positionMenu();
    });
    createEffect(() => {
        if (!shouldShow()) {
            setHoveredOption(() => undefined);
        }
    });
    function addOrRemoveOption(option) {
        const selectedOptions = props.selectedOptions.filter((selectedOption) => props.optionId(selectedOption) !== props.optionId(option));
        if (selectedOptions.length === props.selectedOptions.length) {
            if (selectedOptions.length < props.maxChoices) {
                props.onChange([...selectedOptions, option]);
                if (validatedOnce()) {
                    validate();
                }
            }
        }
        else {
            props.onChange(selectedOptions);
            if (validatedOnce()) {
                validate();
            }
        }
    }
    function handleInputMouseDown() {
        if (isOptionMouseDown()) {
            return;
        }
        setInputMouseDown(true);
        setShouldShow((prev) => !prev);
    }
    function handleInputKeyDown(e) {
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                handleSelectionDown();
                break;
            case 'ArrowUp':
                e.preventDefault();
                handleSelectionUp();
                break;
            case 'Enter':
                e.preventDefault();
                const hoveredOption_ = hoveredOption();
                if (shouldShow()) {
                    if (hoveredOption_) {
                        addOrRemoveOption(hoveredOption_);
                    }
                }
                else {
                    handleFocusByKey();
                }
                break;
        }
    }
    function handleFocusByKey() {
        if (props.selectedOptions?.[0]) {
            setHoveredOption(() => props.selectedOptions[0]);
        }
        setShouldShow((prev) => !prev);
    }
    function handleOptionClick(option, e) {
        e.stopPropagation();
        addOrRemoveOption(option);
    }
    function handleOptionMouseDown() {
        setOptionMouseDown(true);
    }
    function handleOptionMouseUp() {
        setOptionMouseDown(false);
    }
    function handleOptionHover(option) {
        setHoveredOption(() => option);
    }
    function handleSelectionDown() {
        const hoveredOption_ = hoveredOption();
        if (!hoveredOption_) {
            (props.selectedOptions?.[0])
                ? setHoveredOption(() => props.selectedOptions[0])
                : setHoveredOption(() => props.options[0]);
            return;
        }
        const currentIndex = props.options.findIndex((o) => props.optionId(o) === props.optionId(hoveredOption_));
        if (currentIndex === props.options.length - 1) {
            return;
        }
        setHoveredOption(() => props.options[currentIndex + 1]);
    }
    function handleSelectionUp() {
        const hoveredOption_ = hoveredOption();
        if (!hoveredOption_) {
            if (props.selectedOptions?.[0]) {
                setHoveredOption(() => props.selectedOptions[0]);
            }
            return;
        }
        const currentIndex = props.options.findIndex((o) => props.optionId(o) === props.optionId(hoveredOption_));
        if (currentIndex === 0) {
            return;
        }
        setHoveredOption(() => props.options[currentIndex - 1]);
    }
    function isSelectedOption(option) {
        return props.selectedOptions.some((selectedOption) => props.optionId(selectedOption) === props.optionId(option));
    }
    function isHoveredOption(option) {
        const hoveredOption_ = hoveredOption();
        return hoveredOption_ && props.optionId(option) === props.optionId(hoveredOption_);
    }
    function handleBlur() {
        setShouldShow(false);
    }
    return (<div ref={rootRef} class={styles.select} classList={{ [styles.hasError]: !!error() }}>
      <Show when={props.label}>
        <div class={styles.label}>
          {props.label}
        </div>
      </Show>

      <div class={styles.input} tabindex="0" onBlur={handleBlur} onKeyDown={handleInputKeyDown} onMouseDown={handleInputMouseDown} ref={inputElement}>
        <div class={styles.selectedOption}>
          {props.selectedOptions.length > 0
            ? props.renderSelectedOptions(props.selectedOptions)
            : <span class={styles.placeholder}>{props.placeholder}</span>}
        </div>

        <div class={styles.arrow}/>

        <div class={styles.menu} classList={{
            [styles.shouldShow]: shouldShow()
        }} use:clickOutside={() => {
            if (!isInputMouseDown()) {
                setShouldShow(false);
            }
            setInputMouseDown(false);
        }} use:pressEscape={() => setShouldShow(false)}>
          <Index each={props.options}>{(option) => <div class={styles.option} classList={{
                [styles.hoveredOptionInMenu]: isHoveredOption(option()),
                [styles.selectedOptionInMenu]: isSelectedOption(option())
            }} onClick={[handleOptionClick, option()]} onMouseDown={handleOptionMouseDown} onMouseUp={handleOptionMouseUp} onMouseOver={[handleOptionHover, option()]}>
              <span class={styles.optionText}>{props.optionText(option())}</span>
            </div>}</Index>
        </div>
        <div class={styles.borderBottom}/>
      </div>

      <FieldError error={error()}/>
    </div>);
}
function initialOptions(props) {
    if (props.selectedOptions) {
        return props.selectedOptions;
    }
    if (props.placeholder) {
        return [];
    }
    return [props.options[0]];
}
export default MultiSelect;
